import React, { useEffect, useState } from "react";
import axios from "axios";
import MicrosoftSignInButton from "../MicrosoftSignIn/MicrosoftSignInButton";

const API = () => process.env.REACT_APP_API_ROOT;

/**
 * Organisation settings for "Sign in with Microsoft": which Entra tenant may
 * sign in, and whether password sign-in is switched off (SSO only).
 * Shown to settings admins; the API enforces that too.
 */
export default function MicrosoftSignInSettings({ intuneTenantId }) {
  const [state, setState] = useState({ loading: true });
  const [tenant, setTenant] = useState("");
  const [ssoOnly, setSsoOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const apply = (data) => {
    setState({ loading: false, ...data });
    setTenant(data.ssoTenantId || "");
    setSsoOnly(!!data.ssoOnly);
  };

  useEffect(() => {
    axios
      .get(`${API()}/organization/sso`)
      .then((r) => apply(r.data))
      .catch(() => setState({ loading: false, unavailable: true }));
  }, []);

  if (state.loading || state.unavailable) return null;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await axios.post(`${API()}/organization/sso`, { ssoTenantId: tenant, ssoOnly });
      apply(r.data);
      setMessage({ ok: true, text: "Saved." });
    } catch (e) {
      setMessage({ ok: false, text: e?.response?.data?.error?.message || "Couldn't save these settings. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-10 flex flex-col" aria-labelledby="ms-sso-heading">
      <h2 id="ms-sso-heading" className="text-xl font-medium">
        Sign in with Microsoft
      </h2>
      {!state.enabled ? (
        <p className="mt-3 text-sm text-gray-600">Microsoft sign-in isn't available on this Mobstr installation yet.</p>
      ) : (
        <>
          <p className="mt-3 max-w-2xl">
            Let people in your organisation sign in with their Microsoft work account. Only people you have invited to Mobstr can
            sign in, and only with an account from the Microsoft tenant below.
          </p>

          <label htmlFor="sso_tenant" className="mt-5 text-sm font-medium text-gray-900">
            Microsoft tenant ID
          </label>
          <input
            id="sso_tenant"
            type="text"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full md:w-96 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
          />
          {!tenant && intuneTenantId && (
            <button type="button" onClick={() => setTenant(intuneTenantId)} className="mt-2 w-fit text-left text-sm text-mobstr-purple underline">
              Use your Intune tenant ID ({intuneTenantId})
            </button>
          )}

          <label className="mt-5 flex max-w-2xl items-start gap-3">
            <input
              type="checkbox"
              checked={ssoOnly}
              onChange={(e) => setSsoOnly(e.target.checked)}
              disabled={!tenant}
              className="mt-1 rounded border-gray-300 text-mobstr-purple focus:ring-mobstr-purple"
            />
            <span>
              <span className="font-medium">SSO only</span>
              <span className="block text-sm text-gray-600">
                Switch off email and password sign-in for everyone in your organisation. New invitees accept with Microsoft instead
                of setting a password.
              </span>
            </span>
          </label>
          {ssoOnly && !state.ssoOnly && !state.youAreLinked && (
            <div className="mt-3 max-w-2xl rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              To make sure you can't lock yourself out, sign in with Microsoft once before switching this on. Save the tenant ID
              first, then use the button below.
              <div className="mt-3 max-w-xs">
                <MicrosoftSignInButton returnTo="/settings" label="Sign in with Microsoft" />
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center gap-4">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-white w-fit bg-[#2c4251] hover:bg-[#3d627a] disabled:opacity-60 font-medium rounded text-sm px-5 py-2.5 text-center uppercase"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {message && (
              <span role="status" className={`text-sm ${message.ok ? "text-green-700" : "text-red-600"}`}>
                {message.text}
              </span>
            )}
          </div>

          {state.adminConsentUrl && (
            <p className="mt-5 max-w-2xl text-sm text-gray-600">
              If your Microsoft tenant doesn't let users approve apps themselves, a Microsoft Entra administrator needs to{" "}
              <a href={state.adminConsentUrl} className="text-mobstr-purple underline">
                grant consent for Mobstr
              </a>{" "}
              once.
            </p>
          )}
        </>
      )}
    </section>
  );
}
